/**
 * EditModal - Pokemon editing panel
 * Allows customization: ability, item, tera type, nature, moves, EVs, IVs
 */
import { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { formatAbilityName, formatItemName, formatMoveName } from '../../data/pokemonTranslations';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import styles from '../../pages/DexBuilder.module.css';

const MAX_SP_TOTAL = 66;
const MAX_SP_SINGLE = 32;
const MAX_IV = 31;

const NATURES = {
  hardy: { boost: null, drop: null, en: 'Hardy', es: 'Fuerte' },
  lonely: { boost: 'attack', drop: 'defense', en: 'Lonely', es: 'Huraña' },
  brave: { boost: 'attack', drop: 'speed', en: 'Brave', es: 'Audaz' },
  adamant: { boost: 'attack', drop: 'special-attack', en: 'Adamant', es: 'Firme' },
  naughty: { boost: 'attack', drop: 'special-defense', en: 'Naughty', es: 'Pícara' },
  bold: { boost: 'defense', drop: 'attack', en: 'Bold', es: 'Osada' },
  docile: { boost: null, drop: null, en: 'Docile', es: 'Dócil' },
  relaxed: { boost: 'defense', drop: 'speed', en: 'Relaxed', es: 'Plácida' },
  impish: { boost: 'defense', drop: 'special-attack', en: 'Impish', es: 'Agitada' },
  lax: { boost: 'defense', drop: 'special-defense', en: 'Lax', es: 'Floja' },
  timid: { boost: 'speed', drop: 'attack', en: 'Timid', es: 'Miedosa' },
  hasty: { boost: 'speed', drop: 'defense', en: 'Hasty', es: 'Activa' },
  serious: { boost: null, drop: null, en: 'Serious', es: 'Seria' },
  jolly: { boost: 'speed', drop: 'special-attack', en: 'Jolly', es: 'Alegre' },
  naive: { boost: 'speed', drop: 'special-defense', en: 'Naive', es: 'Ingenua' },
  modest: { boost: 'special-attack', drop: 'attack', en: 'Modest', es: 'Modesta' },
  mild: { boost: 'special-attack', drop: 'defense', en: 'Mild', es: 'Afable' },
  quiet: { boost: 'special-attack', drop: 'speed', en: 'Quiet', es: 'Mansa' },
  bashful: { boost: null, drop: null, en: 'Bashful', es: 'Tímida' },
  rash: { boost: 'special-attack', drop: 'special-defense', en: 'Rash', es: 'Alocada' },
  calm: { boost: 'special-defense', drop: 'attack', en: 'Calm', es: 'Serena' },
  gentle: { boost: 'special-defense', drop: 'defense', en: 'Gentle', es: 'Amable' },
  sassy: { boost: 'special-defense', drop: 'speed', en: 'Sassy', es: 'Grosera' },
  careful: { boost: 'special-defense', drop: 'special-attack', en: 'Careful', es: 'Cauta' },
  quirky: { boost: null, drop: null, en: 'Quirky', es: 'Rara' }
};

const STAT_SHORT = {
  'attack': 'Atk',
  'defense': 'Def',
  'special-attack': 'SpA',
  'special-defense': 'SpD',
  'speed': 'Spe',
};

function NatureSelector({ value, onChange, language }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const selected = NATURES[value] || NATURES.hardy;
  const langKey = language === 'es' ? 'es' : 'en';

  function renderLabel(data, compact = false) {
    const name = data[langKey];
    if (!data.boost && !data.drop) {
      return (
        <span style={{ color: 'var(--muted-fg, #9ca3af)' }}>
          {name}
          {!compact && <span style={{ fontSize: '0.75em', marginLeft: '0.4em' }}>—</span>}
        </span>
      );
    }
    return (
      <span>
        {name}
        {' '}
        <span style={{ color: '#16a34a', fontWeight: 700 }}>+{STAT_SHORT[data.boost]}</span>
        {' '}
        <span style={{ color: '#dc2626', fontWeight: 700 }}>-{STAT_SHORT[data.drop]}</span>
      </span>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setOpen(o => !o); }}
        style={{
          width: '100%',
          padding: '0.5rem 0.75rem',
          borderRadius: '0.375rem',
          border: '1px solid var(--border-color, #ccc)',
          fontSize: '0.875rem',
          cursor: 'pointer',
          background: 'var(--bg, white)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          userSelect: 'none',
        }}
      >
        <span>{renderLabel(selected, true)}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: '0.5rem', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {open && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          maxHeight: '220px',
          overflowY: 'auto',
          background: 'var(--card-bg, #fff)',
          border: '1px solid var(--border-color, #ccc)',
          borderRadius: '0.375rem',
          boxShadow: '0 6px 16px rgba(0,0,0,0.15)',
          zIndex: 300,
        }}>
          {Object.entries(NATURES).map(([key, data]) => (
            <div
              key={key}
              role="option"
              aria-selected={value === key}
              onClick={() => { onChange(key); setOpen(false); }}
              style={{
                padding: '0.4rem 0.75rem',
                cursor: 'pointer',
                fontSize: '0.875rem',
                background: value === key ? 'rgba(124,58,237,0.08)' : 'transparent',
                borderBottom: '1px solid var(--border-color, #f0f0f0)',
              }}
              onMouseEnter={e => { if (value !== key) e.currentTarget.style.background = 'var(--muted-bg, #f3f4f6)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = value === key ? 'rgba(124,58,237,0.08)' : 'transparent'; }}
            >
              {renderLabel(data)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function EditModal({ isOpen, onClose, slot, onSave, pokemonData, language: forcedLanguage }) {
  const { language } = useApp();
  const lang = forcedLanguage || language;

  // Normalize item to always be a string (slug) for consistent state management
  const getItemSlug = (itemData) => {
    if (!itemData) return '';
    if (typeof itemData === 'string') return itemData;
    if (typeof itemData === 'object' && itemData.name) return itemData.name;
    return '';
  };

  // All hooks must be called unconditionally at top level
  const [nickname, setNickname] = useState(slot?.nickname || '');
  const [level, setLevel] = useState(slot?.level || 50);
  const [shiny, setShiny] = useState(slot?.shiny || false);
  const [nature, setNature] = useState(slot?.nature || 'hardy');
  const [ability, setAbility] = useState(slot?.ability || '');
  const [item, setItem] = useState(getItemSlug(slot?.item) || '');
  const [teraType, setTeraType] = useState(slot?.teraType || '');
  const [moves, setMoves] = useState(slot?.moves || ['', '', '', '']);
  const [evs, setEvs] = useState(slot?.evs || { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 });
  const [ivs, setIvs] = useState(slot?.ivs || { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 });

  const [abilities, setAbilities] = useState([]);
  const [items, setItems] = useState([]);
  const [pokemonMoves, setPokemonMoves] = useState([]);
  const [itemSearch, setItemSearch] = useState('');

  // Complete list of competitive/holdable items
  const FULL_ITEMS_LIST = [
    // Choice items
    'choice-band', 'choice-scarf', 'choice-specs',
    // Recovery/survival
    'leftovers', 'black-sludge', 'shell-bell', 'big-root',
    // Offensive items
    'life-orb', 'expert-belt', 'muscle-band', 'wise-glasses', 'metronome',
    // Defensive items
    'assault-vest', 'eviolite', 'rocky-helmet', 'safety-goggles', 'protective-pads',
    // Priority/Speed
    'focus-sash', 'quick-claw', 'custap-berry', 'lagging-tail', 'iron-ball',
    // Status/Utility
    'heavy-duty-boots', 'air-balloon', 'shed-shell', 'utility-umbrella', 'covert-cloak',
    'clear-amulet', 'eject-button', 'eject-pack', 'red-card',
    // Herbs
    'mental-herb', 'power-herb', 'white-herb', 'mirror-herb',
    // Boost items
    'weakness-policy', 'blunder-policy', 'throat-spray', 'room-service',
    'absorb-bulb', 'cell-battery', 'luminous-moss', 'snowball',
    'electric-seed', 'grassy-seed', 'misty-seed', 'psychic-seed',
    // Terrain/Weather
    'terrain-extender', 'heat-rock', 'damp-rock', 'smooth-rock', 'icy-rock', 'light-clay',
    // Orbs
    'toxic-orb', 'flame-orb', 'life-orb',
    // Lenses
    'scope-lens', 'wide-lens', 'zoom-lens',
    // Claws/Fangs
    'razor-claw', 'razor-fang', 'quick-claw', 'grip-claw',
    // Type-boosting items
    'charcoal', 'mystic-water', 'magnet', 'miracle-seed', 'never-melt-ice',
    'black-belt', 'poison-barb', 'soft-sand', 'sharp-beak', 'twisted-spoon',
    'silver-powder', 'hard-stone', 'spell-tag', 'dragon-fang', 'black-glasses',
    'metal-coat', 'silk-scarf', 'fairy-feather',
    // Plates
    'pixie-plate', 'draco-plate', 'dread-plate', 'earth-plate', 'fist-plate',
    'flame-plate', 'icicle-plate', 'insect-plate', 'iron-plate', 'meadow-plate',
    'mind-plate', 'sky-plate', 'splash-plate', 'spooky-plate', 'stone-plate',
    'toxic-plate', 'zap-plate', 'blank-plate',
    // Incenses
    'full-incense', 'lax-incense', 'luck-incense', 'odd-incense', 'pure-incense',
    'rock-incense', 'rose-incense', 'sea-incense', 'wave-incense',
    // Gems
    'normal-gem', 'fire-gem', 'water-gem', 'electric-gem', 'grass-gem', 'ice-gem',
    'fighting-gem', 'poison-gem', 'ground-gem', 'flying-gem', 'psychic-gem',
    'bug-gem', 'rock-gem', 'ghost-gem', 'dragon-gem', 'dark-gem', 'steel-gem', 'fairy-gem',
    // Mega Stones
    'abomasite', 'absolite', 'aerodactylite', 'aggronite', 'alakazite', 'altarianite',
    'ampharosite', 'audinite', 'banettite', 'beedrillite', 'blastoisinite', 'blazikenite',
    'cameruptite', 'charizardite-x', 'charizardite-y', 'diancite', 'galladite',
    'garchompite', 'gardevoirite', 'gengarite', 'glalitite', 'gyaradosite',
    'heracronite', 'houndoominite', 'kangaskhanite', 'latiasite', 'latiosite',
    'lopunnite', 'lucarionite', 'manectite', 'mawilite', 'medichamite', 'metagrossite',
    'mewtwonite-x', 'mewtwonite-y', 'pidgeotite', 'pinsirite', 'sablenite', 'salamencite',
    'sceptilite', 'scizorite', 'sharpedonite', 'slowbronite', 'steelixite', 'swampertite',
    'tyranitarite', 'venusaurite',
    // Z-Crystals
    'buginium-z', 'darkinium-z', 'dragonium-z', 'electrium-z', 'fairium-z', 'fightinium-z',
    'firium-z', 'flyinium-z', 'ghostium-z', 'grassium-z', 'groundium-z', 'icium-z',
    'normalium-z', 'poisonium-z', 'psychium-z', 'rockium-z', 'steelium-z', 'waterium-z',
    'aloraichium-z', 'decidium-z', 'eevium-z', 'incinium-z', 'kommonium-z', 'lunalium-z',
    'lycanium-z', 'marshadium-z', 'mewnium-z', 'mimikium-z', 'pikanium-z', 'pikashunium-z',
    'primarium-z', 'snorlium-z', 'solganium-z', 'tapunium-z', 'ultranecrozium-z',
    // Species-specific
    'light-ball', 'thick-club', 'lucky-punch', 'leek', 'stick', 'metal-powder', 'quick-powder',
    'deep-sea-tooth', 'deep-sea-scale', 'soul-dew', 'adamant-orb', 'lustrous-orb',
    'griseous-orb', 'griseous-core', 'adamant-crystal', 'lustrous-globe',
    // Gen 8-9 items
    'booster-energy', 'loaded-dice', 'punching-glove', 'ability-shield',
    // Berries - Healing
    'oran-berry', 'sitrus-berry', 'figy-berry', 'wiki-berry', 'mago-berry', 'aguav-berry', 'iapapa-berry', 'berry-juice',
    // Berries - Status
    'cheri-berry', 'chesto-berry', 'pecha-berry', 'rawst-berry', 'aspear-berry', 'persim-berry', 'lum-berry', 'leppa-berry',
    // Berries - Stat boost
    'liechi-berry', 'ganlon-berry', 'salac-berry', 'petaya-berry', 'apicot-berry',
    'lansat-berry', 'starf-berry', 'enigma-berry', 'micle-berry', 'custap-berry',
    'jaboca-berry', 'rowap-berry', 'kee-berry', 'maranga-berry',
    // Berries - Type resist
    'occa-berry', 'passho-berry', 'wacan-berry', 'rindo-berry', 'yache-berry',
    'chople-berry', 'kebia-berry', 'shuca-berry', 'coba-berry', 'payapa-berry',
    'tanga-berry', 'charti-berry', 'kasib-berry', 'haban-berry', 'colbur-berry',
    'babiri-berry', 'roseli-berry', 'chilan-berry',
    // Other useful
    'bright-powder', 'kings-rock', 'amulet-coin', 'luck-incense', 'binding-band',
    'focus-band', 'destiny-knot', 'red-card', 'ring-target', 'sticky-barb',
    'float-stone', 'macho-brace', 'power-weight', 'power-bracer', 'power-belt',
    'power-lens', 'power-band', 'power-anklet'
  ].sort();

  useEffect(() => {
    if (!pokemonData) return;

    try {
      // Load abilities from Pokemon data
      if (pokemonData?.abilities) {
        setAbilities(pokemonData.abilities.map(a => a.ability.name));
      }

      // Load ALL moves from Pokemon data
      if (pokemonData?.moves) {
        const allMoves = pokemonData.moves.map(m => m.move.name).sort((a, b) => a.localeCompare(b));
        setPokemonMoves(allMoves);
      }
    } catch (error) {
      console.error('Error processing pokemon data:', error);
    }

    // Use pre-defined items list (instant load, no API calls)
    setItems(FULL_ITEMS_LIST);
  }, [pokemonData]);

  // Create reverse mappings for save logic
  const moveNameToSlug = pokemonMoves.reduce((acc, m) => {
    acc[formatMoveName(m, lang)] = m;
    return acc;
  }, {});

  const itemNameToSlug = items.reduce((acc, i) => {
    acc[formatItemName(i, lang)] = i;
    return acc;
  }, {});

  // Early return for conditional rendering (after all hooks)
  if (!isOpen || !pokemonData) return null;

  const handleSave = () => {
    // Validate EVs total
    const evTotal = Object.values(evs).reduce((a, b) => a + b, 0);
    if (evTotal > MAX_SP_TOTAL) {
      toast.error(lang === 'es' ? 'Total SP excede 66' : 'SP total exceeds 66');
      return;
    }

    // Resolve item slug: try reverse mapping with display name, then use as-is if it's a slug
    const displayName = formatItemName(item, lang);
    const resolvedItem = itemNameToSlug[displayName] || item || '';

    // Resolve move slugs: try reverse mapping with display name, then use as-is if it's a slug
    const resolvedMoves = moves.map(m => {
      const displayMoveName = formatMoveName(m, lang);
      return moveNameToSlug[displayMoveName] || m || '';
    });

    const updatedSlot = {
      ...createEmptySlot(),
      id: pokemonData.id,
      name: pokemonData.name,
      nickname,
      level: Math.max(1, Math.min(100, parseInt(level) || 50)),
      gender: slot?.gender || 'random',
      nature,
      ability: ability || pokemonData.abilities?.[0]?.ability.name || '',
      item: {
        name: resolvedItem,
        sprite: resolvedItem ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/items/${resolvedItem}.png` : ''
      },
      teraType,
      moves: resolvedMoves,
      evs,
      ivs,
      shiny,
      data: pokemonData
    };

    onSave(updatedSlot);
    toast.success(lang === 'es' ? 'Pokémon guardado' : 'Pokémon saved');
  };

  // Helper for empty slot defaults (matching DexBuilder's createEmptySlot)
  function createEmptySlot() {
    return {
      id: null, name: '', nickname: '', level: 50, gender: 'random',
      nature: 'hardy', ability: '', item: { name: '', sprite: '' }, teraType: '',
      moves: ['', '', '', ''],
      evs: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
      ivs: { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
      shiny: false, data: null
    };
  }

  const updateEv = (stat, value) => {
    const newValue = Math.max(0, Math.min(MAX_SP_SINGLE, parseInt(value) || 0));
    const otherTotal = Object.entries(evs)
      .reduce((sum, [key, val]) => key !== stat ? sum + val : sum, 0);

    // Auto-clamp if user tries to go over (optional, showing red warning is also valid, but user asked for auto-clamp)
    let finalValue = newValue;
    if (otherTotal + finalValue > MAX_SP_TOTAL) {
      finalValue = MAX_SP_TOTAL - otherTotal;
    }

    setEvs({ ...evs, [stat]: finalValue });
  };

  const updateIv = (stat, value) => {
    setIvs({ ...ivs, [stat]: Math.max(0, Math.min(MAX_IV, parseInt(value) || 0)) });
  };

  const updateMove = (index, value) => {
    const newMoves = [...moves];
    newMoves[index] = value;
    setMoves(newMoves);
  };

  return (
    <div className={styles.modal} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
            {lang === 'es' ? 'Editar Pokémon' : 'Edit Pokémon'}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ display: 'grid', gap: '1rem' }}>
          {/* Basic Info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', display: 'block', marginBottom: '0.375rem' }}>
                {lang === 'es' ? 'Apodo' : 'Nickname'}
              </label>
              <Input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder={pokemonData.name}
              />
            </div>
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', display: 'block', marginBottom: '0.375rem' }}>
                {lang === 'es' ? 'Nivel' : 'Level'}
              </label>
              <Input
                type="number"
                min="1"
                max="100"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
              />
            </div>
          </div>

          {/* Ability, Item, Tera */}
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', display: 'block', marginBottom: '0.375rem' }}>
              {lang === 'es' ? 'Habilidad' : 'Ability'}
            </label>
            <select
              value={ability}
              onChange={(e) => setAbility(e.target.value)}
              style={{
                width: '100%',
                padding: '0.5rem',
                borderRadius: '0.375rem',
                border: '1px solid var(--border-color, #ccc)',
                fontSize: '0.875rem'
              }}
            >
              <option value="">{lang === 'es' ? 'Por defecto' : 'Default'}</option>
              {abilities.map(a => (
                <option key={a} value={a}>{formatAbilityName(a, lang)}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div style={{ position: 'relative' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', display: 'block', marginBottom: '0.375rem' }}>
                {lang === 'es' ? 'Objeto' : 'Item'}
              </label>
              <Input
                value={itemSearch || formatItemName(item, lang)}
                onChange={(e) => {
                  setItemSearch(e.target.value);
                  // If exact match found, set it
                  const searchLower = e.target.value.toLowerCase();
                  const exactMatch = items.find(i => 
                    formatItemName(i, lang).toLowerCase() === searchLower ||
                    i.toLowerCase() === searchLower
                  );
                  if (exactMatch) {
                    setItem(exactMatch);
                  } else if (e.target.value === '') {
                    setItem('');
                  }
                }}
                onFocus={() => setItemSearch('')}
                onBlur={() => setTimeout(() => setItemSearch(''), 200)}
                placeholder={lang === 'es' ? 'Buscar objeto...' : 'Search item...'}
              />
              {itemSearch && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  maxHeight: '200px',
                  overflowY: 'auto',
                  background: 'var(--card-bg, #fff)',
                  border: '1px solid var(--border-color, #ccc)',
                  borderRadius: '0.375rem',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 100
                }}>
                  {items
                    .filter(i => {
                      const search = itemSearch.toLowerCase();
                      return i.toLowerCase().includes(search) || 
                             formatItemName(i, lang).toLowerCase().includes(search);
                    })
                    .slice(0, 50)
                    .map(i => (
                      <div
                        key={i}
                        onClick={() => {
                          setItem(i);
                          setItemSearch('');
                        }}
                        style={{
                          padding: '0.5rem 0.75rem',
                          cursor: 'pointer',
                          fontSize: '0.875rem',
                          borderBottom: '1px solid var(--border-color, #eee)',
                          transition: 'background 0.1s'
                        }}
                        onMouseEnter={(e) => e.target.style.background = 'var(--muted-bg, #f3f4f6)'}
                        onMouseLeave={(e) => e.target.style.background = 'transparent'}
                      >
                        {formatItemName(i, lang)}
                      </div>
                    ))
                  }
                  {items.filter(i => {
                    const search = itemSearch.toLowerCase();
                    return i.toLowerCase().includes(search) || 
                           formatItemName(i, lang).toLowerCase().includes(search);
                  }).length === 0 && (
                    <div style={{ padding: '0.5rem 0.75rem', color: 'var(--muted-fg)', fontSize: '0.875rem' }}>
                      {lang === 'es' ? 'No se encontraron objetos' : 'No items found'}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label style={{ fontSize: '0.875rem', fontWeight: '600', display: 'block', marginBottom: '0.375rem' }}>
                {lang === 'es' ? 'Tipo Tera' : 'Tera Type'}
              </label>
              <select
                value={teraType}
                onChange={(e) => setTeraType(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid var(--border-color, #ccc)',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">{lang === 'es' ? 'Ninguno' : 'None'}</option>
                {Object.keys({
                  normal: '', fire: '', water: '', electric: '', grass: '', ice: '',
                  fighting: '', poison: '', ground: '', flying: '', psychic: '', bug: '',
                  rock: '', ghost: '', dragon: '', dark: '', steel: '', fairy: ''
                }).map(type => (
                  <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Nature */}
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', display: 'block', marginBottom: '0.375rem' }}>
              {lang === 'es' ? 'Naturaleza' : 'Nature'}
            </label>
            <NatureSelector value={nature} onChange={setNature} language={lang} />
          </div>

          {/* Moves */}
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', display: 'block', marginBottom: '0.375rem' }}>
              {lang === 'es' ? 'Movimientos (máx 4)' : 'Moves (max 4)'}
            </label>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {moves.map((move, i) => (
                <div key={i}>
                  <Input
                    value={formatMoveName(move, lang)}
                    onChange={(e) => updateMove(i, e.target.value)}
                    placeholder={lang === 'es' ? `Movimiento ${i + 1}...` : `Move ${i + 1}...`}
                    list={`moves-list-${i}`}
                  />
                  <datalist id={`moves-list-${i}`}>
                    {pokemonMoves.map(m => (
                      <option key={m} value={formatMoveName(m, lang)} />
                    ))}
                  </datalist>
                </div>
              ))}
            </div>
          </div>

          {/* SP (Stat Points) Section */}
          <div style={{
            background: 'rgba(124, 58, 237, 0.05)',
            padding: '1rem',
            borderRadius: '0.75rem',
            border: '1px solid rgba(124, 58, 237, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.875rem', fontWeight: '700', color: 'var(--primary-color)' }}>
                {lang === 'es' ? 'SP (Puntos de Estadística)' : 'SP (Stat Points)'}
              </label>
              <div style={{ 
                fontSize: '0.875rem', 
                fontWeight: '800', 
                color: Object.values(evs).reduce((a, b) => a + b, 0) > MAX_SP_TOTAL ? '#ef4444' : 'var(--primary-color)'
              }}>
                {Object.values(evs).reduce((a, b) => a + b, 0)} / {MAX_SP_TOTAL}
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.75rem' }}>
              {['hp', 'atk', 'def', 'spa', 'spd', 'spe'].map(stat => (
                <div key={stat}>
                  <label style={{ fontSize: '0.7rem', fontWeight: '700', display: 'block', marginBottom: '0.25rem', color: 'var(--muted-fg)' }}>
                    {stat.toUpperCase()}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="32"
                    value={evs[stat] || 0}
                    onChange={(e) => updateEv(stat, e.target.value)}
                    style={{ fontSize: '0.875rem', textAlign: 'center', fontWeight: 'bold' }}
                  />
                </div>
              ))}
            </div>
            {Object.values(evs).reduce((a, b) => a + b, 0) > MAX_SP_TOTAL && (
              <div style={{ fontSize: '0.7rem', color: '#ef4444', marginTop: '0.5rem', fontWeight: '600', textAlign: 'center' }}>
                {lang === 'es' ? '¡Has excedido el presupuesto de 66 SP!' : 'Exceeded 66 SP budget!'}
              </div>
            )}
          </div>

          {/* IVs */}
          <div>
            <label style={{ fontSize: '0.875rem', fontWeight: '600', display: 'block', marginBottom: '0.375rem' }}>
              {lang === 'es' ? 'IVs (máx 31 c/u)' : 'IVs (max 31 each)'}
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.5rem' }}>
              {['hp', 'atk', 'def', 'spa', 'spd', 'spe'].map(stat => (
                <div key={stat}>
                  <label style={{ fontSize: '0.75rem', display: 'block', marginBottom: '0.25rem' }}>
                    {stat.toUpperCase()}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="31"
                    value={ivs[stat] || 31}
                    onChange={(e) => updateIv(stat, e.target.value)}
                    style={{ fontSize: '0.875rem' }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Shiny checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <input
              type="checkbox"
              id="shiny"
              checked={shiny}
              onChange={(e) => setShiny(e.target.checked)}
            />
            <label htmlFor="shiny" style={{ fontSize: '0.875rem', cursor: 'pointer' }}>
              {lang === 'es' ? '✨ Forma Variocolor' : '✨ Shiny'}
            </label>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1.5rem' }}>
            <Button variant="outline" onClick={onClose}>
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </Button>
            <Button onClick={handleSave} style={{ backgroundColor: 'var(--primary-color, #7c3aed)', color: 'white' }}>
              {lang === 'es' ? 'Guardar' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditModal;
